import React, { useRef, useCallback, useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import moment from "moment";

const config = require("./../../configs/app");

const processData = (data) => {
    const processedData = [];
    let sum = 0;
    let count = 0;

    for (let i = 0; i < data.length; i++) {
        const [time, value] = data[i];
        sum += value;
        count++;

        if (time >= 86400) break;

        if (i === data.length - 1 || time % 60 === 0) {
            const mean = sum / count;
            processedData.push([time, Number(mean.toFixed(4))]); // เก็บเป็น number
            sum = 0;
            count = 0;
        }
    }
    return processedData;
};

const ChartComponent = ({ dataOrg }) => {
    const [series, setSeries] = useState([{ name: "Electric Field", data: [], color: "#d71d24" }]);
    const [minMax, setMinMax] = useState(0);
    const [loading, setLoading] = useState(false);

    const canvasRef = useRef(null);
    const isFetchingRef = useRef(false);

    const fetchData = useCallback(async () => {
        if (!dataOrg) return;
        if (isFetchingRef.current) return; // กันไม่ให้ซ้อน

        isFetchingRef.current = true;
        try {
            const res = await fetch(`${config.urlLightningConfig}/api/v1/e-field/${dataOrg}`);
            const json = await res.json();
            const processedData = processData(json?.message?.d || []);
            if (processedData.length === 0) return;

            setSeries((prev) => [{ ...prev[0], data: processedData }]);

            const yValues = processedData.map((p) => p[1]);
            const maxVal = Math.max(...yValues.map(Math.abs));
            if (!isNaN(maxVal) && maxVal !== Infinity) {
                setMinMax((prev) => (prev !== maxVal ? maxVal : prev));
            }
        } catch (err) {
            console.error(err);
        } finally {
            isFetchingRef.current = false;
        }
    }, [dataOrg]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const options = {
        chart: { id: "time-value-chart", type: "line", zoom: { enabled: false } },
        xaxis: {
            type: "numeric",
            labels: {
                formatter: (value) => {
                    const offset = moment().utcOffset() / 60;
                    return moment
                        .utc(value * 1000)
                        .add(offset - 7, "hours")
                        .format("HH:mm:ss");
                },
            },
        },
        yaxis: {
            min: -minMax - 0.2,
            max: minMax + 0.2,
            tickAmount: 4,
            forceNiceScale: true,
            labels: { formatter: (value) => `${value.toFixed(2)}` },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        grid: {
            show: true,
            borderColor: "#90A4AE",
            strokeDashArray: 1,
            position: "front",
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: false } },
            padding: { top: 0, right: 0, bottom: 0, left: 10 },
        },
        annotations: {
            yaxis: [
                {
                    y: 0,
                    borderColor: "#000",
                    borderWidth: 0.5,
                    opacity: 0.5,
                    label: {
                        text: "kV/m",
                        position: "top",
                        offsetY: -43,
                    },
                },
            ],
        },
        stroke: { curve: "smooth", width: 2 },
    };

    return (
        <div style={{ height: "84%", width: "100%", position: "relative" }}>
            {loading && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(255,255,255,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                    }}
                >
                    <span>Loading...</span>
                </div>
            )}
            <ReactApexChart options={options} series={series} type="line" height="100%" ref={canvasRef} />
        </div>
    );
};

export default ChartComponent;
